import { signal } from "@preact/signals";
import { useState } from "preact/hooks";
import {
  RANGES,
  SENS_UNITS,
  cm360ToCs,
  convertSensitivity,
  csToCm360,
  degreesPerCount,
  formatSensitivity,
  parseDecimal,
  settings,
  updateSettings,
} from "../../../settings";
import type { SensUnit } from "../../../settings";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { Segmented } from "../../components/Segmented";
import type { SegmentedOption } from "../../components/Segmented";
import { cx } from "../../cx";

type Source = SensUnit | "cm360";

const SOURCES: readonly SegmentedOption<Source>[] = [
  ...SENS_UNITS.map((u) => ({ value: u.id, label: u.label })),
  { value: "cm360", label: "cm / 360°" },
];

const DPI_PRESETS: readonly number[] = [400, 800, 1600, 3200];
const MAX_DPI = 100_000;

/** Mouse DPI used for every cm/360 figure of the screen. Session only, not part of the settings. */
export const sensDpi = signal(800);

/** "34.2" (two decimals below 10 cm); "-" when there is no finite distance. */
export function formatCm360(cm: number): string {
  if (!Number.isFinite(cm)) return "-";
  return cm.toFixed(cm < 10 ? 2 : 1);
}

/** Editable text with up to 5 significant digits and no trailing zeros. */
function plain(n: number): string {
  return String(Number(n.toPrecision(5)));
}

/** CS-equivalent sensitivity described by the inputs, or null when they do not describe one. */
function csFromInput(source: Source, value: number | null, dpi: number | null): number | null {
  if (value === null || !(value > 0)) return null;
  if (source === "cm360") {
    if (dpi === null) return null;
    const cs = cm360ToCs(value, dpi);
    return Number.isFinite(cs) ? cs : null;
  }
  return convertSensitivity(value, source, "cs");
}

function textFor(source: Source, cs: number, dpi: number | null): string {
  if (source === "cm360") return dpi === null ? "" : plain(csToCm360(cs, dpi));
  return plain(convertSensitivity(cs, "cs", source));
}

/**
 * Converts a sensitivity from another game (or from a physical distance per 360° turn and the
 * mouse DPI) to the CS-equivalent value stored in the settings, and shows it in every unit.
 */
export function SensitivityConverter({ onClose }: { onClose: () => void }) {
  const current = settings.value.controls.sensitivity;
  const [source, setSource] = useState<Source>("cs");
  const [text, setText] = useState(() => plain(current));
  const [dpiText, setDpiText] = useState(() => String(sensDpi.value));

  const dpiInput = parseDecimal(dpiText);
  const dpi = dpiInput !== null && dpiInput > 0 && dpiInput <= MAX_DPI ? dpiInput : null;
  const value = parseDecimal(text);
  const cs = csFromInput(source, value, dpi);

  const changeDpi = (next: string) => {
    setDpiText(next);
    const n = parseDecimal(next);
    if (n !== null && n > 0 && n <= MAX_DPI) sensDpi.value = n;
  };

  const changeSource = (next: Source) => {
    if (next === source) return;
    setSource(next);
    setText(textFor(next, cs ?? current, dpi));
  };

  const apply = () => {
    if (cs === null) return;
    updateSettings({ controls: { sensitivity: cs } });
    onClose();
  };

  const onEnter = (e: KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    apply();
  };

  const { min, max } = RANGES.sensitivity;
  const outOfRange = cs !== null && (cs < min || cs > max);
  const sourceLabel = SOURCES.find((s) => s.value === source)?.label ?? "";

  let valueError: string | undefined;
  if (value === null || !(value > 0)) valueError = "Enter a number greater than 0.";
  else if (cs === null) valueError = "Enter a valid DPI to convert from cm / 360°.";

  return (
    <div class="settings__conv">
      <Segmented<Source> ariaLabel="Convert from" value={source} options={SOURCES} onChange={changeSource} />

      <div class="settings__convinputs">
        <Field
          label={source === "cm360" ? "Mouse travel per 360° turn (cm)" : `${sourceLabel} sensitivity`}
          error={valueError}
          hint={outOfRange ? `Outside ${min} to ${max} (CS2), Apply clamps it.` : undefined}
        >
          {(id) => (
            <input
              id={id}
              class={cx("input", valueError !== undefined && "is-invalid")}
              type="text"
              inputMode="decimal"
              value={text}
              aria-invalid={valueError !== undefined || undefined}
              data-autofocus=""
              data-nav=""
              spellcheck={false}
              onFocus={(e) => e.currentTarget.select()}
              onInput={(e) => setText(e.currentTarget.value)}
              onKeyDown={onEnter}
            />
          )}
        </Field>
        <Field label="Mouse DPI" error={dpi === null ? "Enter 1 to 100000." : undefined}>
          {(id) => (
            <input
              id={id}
              class={cx("input", dpi === null && "is-invalid")}
              type="text"
              inputMode="numeric"
              value={dpiText}
              aria-invalid={dpi === null || undefined}
              data-nav=""
              spellcheck={false}
              onFocus={(e) => e.currentTarget.select()}
              onInput={(e) => changeDpi(e.currentTarget.value)}
              onKeyDown={onEnter}
            />
          )}
        </Field>
      </div>

      <div class="settings__chips" role="group" aria-label="DPI presets">
        {DPI_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            class="settings__chip"
            aria-pressed={dpi === preset}
            data-nav=""
            onClick={() => changeDpi(String(preset))}
          >
            {preset} DPI
          </button>
        ))}
      </div>

      <dl class="settings__convlist" aria-live="polite">
        {SENS_UNITS.map((u) => (
          <div key={u.id} class="settings__convrow">
            <dt>{u.label}</dt>
            <dd class="num">{cs === null ? "-" : formatSensitivity(convertSensitivity(cs, "cs", u.id))}</dd>
          </div>
        ))}
        <div class="settings__convrow">
          <dt>cm / 360° at {dpi ?? "-"} DPI</dt>
          <dd class="num">{cs === null || dpi === null ? "-" : formatCm360(csToCm360(cs, dpi))}</dd>
        </div>
        <div class="settings__convrow">
          <dt>Degrees per count</dt>
          <dd class="num">{cs === null ? "-" : degreesPerCount(cs).toFixed(5)}</dd>
        </div>
      </dl>

      <p class="settings__convcurrent muted num">
        Current setting: {formatSensitivity(current)} CS2, {degreesPerCount(current).toFixed(5)}°/count,{" "}
        {dpi === null ? "-" : formatCm360(csToCm360(current, dpi))} cm / 360°
      </p>

      <div class="settings__convactions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={cs === null} onClick={apply}>
          Apply
        </Button>
      </div>
    </div>
  );
}
